import type { Action } from "./bindings";

/** What the player is doing, which decides the buttons shown. */
export type TouchMode = "foot" | "helm" | "turret";
/** What a finger on the world does on foot: hold to dig (and swing), or tap to place (and use the helm, turrets, hatches, chests, furnaces). */
export type TouchTool = "dig" | "place";
export type TouchPreference = "auto" | "on" | "off";

/** Buttons held down rather than pressed. */
type Hold = "jump" | "fire" | "turnLeft" | "turnRight";

interface ButtonDef {
  id: string;
  label: string;
  /** Held: on while the finger is down. */
  hold?: Hold;
  /** Pressed: an action, as a key press of it. */
  press?: Action;
  /** Pressed: leave the helm or the turret (a right click, on a mouse). */
  leave?: true;
  size?: "big" | "wide";
}

const FOOT: ButtonDef[] = [
  { id: "jump", label: "Jump", hold: "jump", size: "big" },
  { id: "grab", label: "Grab", press: "hook" },
  { id: "tool", label: "" },
  { id: "rotate", label: "Rotate", press: "rotate" },
];
const HELM: ButtonDef[] = [
  { id: "turnR", label: "↻", hold: "turnRight", size: "big" },
  { id: "turnL", label: "↺", hold: "turnLeft", size: "big" },
  { id: "fire", label: "Fire", hold: "fire" },
  { id: "leave", label: "Leave helm", leave: true, size: "wide" },
];
const TURRET: ButtonDef[] = [
  { id: "fire", label: "Fire", hold: "fire", size: "big" },
  { id: "grab", label: "Let go", press: "hook" },
  { id: "out", label: "Climb out", leave: true, size: "wide" },
];
const BUTTONS: Record<TouchMode, ButtonDef[]> = { foot: FOOT, helm: HELM, turret: TURRET };

/** The stick's throw (px) for full tilt, and below what share of it the stick counts as centred. */
const STICK_RADIUS = 56;
const STICK_DEAD = 0.15;
/** Tilting the stick up past this jumps or climbs (a nudge doesn't); down past it climbs down. */
const STICK_STEP = 0.5;

/**
 * Touch controls, over the game on phones and tablets: a stick where the
 * left thumb lands, buttons for the right thumb that change with what you
 * are doing (on foot, at the helm, in a turret), a finger on the world to
 * aim, dig or place, and two to zoom. Input reads it each step the way it
 * reads the gamepad.
 */
export class TouchControls {
  /** An action button was pressed (one-shot, like a key). */
  onPress: (action: Action) => void = () => {};
  /** Leave helm or Climb out was pressed. */
  onLeave: () => void = () => {};
  /** A finger on the world moved: the pointer, in client px. */
  onPointer: (x: number, y: number) => void = () => {};
  /** Two fingers moved apart or together: the camera distance, times this. */
  onZoom: (factor: number) => void = () => {};
  onChat: () => void = () => {};
  onHelp: () => void = () => {};
  /** Open the big map (the small one is hidden on short screens). */
  onMap: () => void = () => {};
  /** Shown or hidden (the setting, or a touch screen turning out to be in use). */
  onEnabled: (on: boolean) => void = () => {};

  enabled = false;
  mode: TouchMode = "foot";
  tool: TouchTool = "dig";
  /** The stick's tilt, each -1..1 (y up). */
  readonly move = { x: 0, y: 0 };
  /** A finger is on the world (not on the stick or a button). */
  worldDown = false;

  private preference: TouchPreference = "auto";
  private readonly held = new Set<Hold>();
  private readonly root = document.createElement("div");
  private readonly stickZone = document.createElement("div");
  private readonly stick = document.createElement("div");
  private readonly knob = document.createElement("div");
  private readonly cluster = document.createElement("div");
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private stickPointer: number | null = null;
  private stickAt = { x: 0, y: 0 };
  /** Fingers on the world, by pointer id; two of them pinch. */
  private readonly fingers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;

  constructor(hud: HTMLElement) {
    this.root.id = "touch";
    this.stickZone.className = "touch-stick-zone";
    this.stick.className = "touch-stick";
    this.knob.className = "touch-knob";
    this.stick.append(this.knob);
    this.stickZone.append(this.stick);
    this.cluster.className = "touch-buttons";
    const top = document.createElement("div");
    top.className = "touch-top";
    top.append(
      this.smallButton("Bag", "Inventory and crafting", () => this.onPress("inventory")),
      this.smallButton("Map", "The map", () => this.onMap()),
      this.smallButton("Chat", "Chat", () => this.onChat()),
      this.smallButton("Eat", "Eat (heal)", () => this.onPress("eat")),
      this.smallButton("?", "Controls", () => this.onHelp()),
    );
    if (document.fullscreenEnabled) top.append(this.smallButton("⛶", "Full screen", () => void goFullscreen()));
    this.root.append(this.stickZone, this.cluster, top);
    hud.append(this.root);
    this.wireStick();
    this.buildButtons();
  }

  /** The setting: on, off, or on when the screen is a touch screen (and the moment a finger touches the game). */
  setPreference(p: TouchPreference): void {
    this.preference = p;
    this.setEnabled(p === "on" || (p === "auto" && matchMedia("(pointer: coarse)").matches));
  }

  private setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    this.root.classList.toggle("on", on);
    if (!on) this.releaseAll();
    this.onEnabled(on);
  }

  /** What the player is doing now: the buttons follow. */
  setContext(mode: TouchMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.releaseAll();
    this.buildButtons();
  }

  /** The stick's x, and its y as a step up or down (on foot; at the helm it's the lift, as it is). */
  get moveY(): number {
    if (this.mode !== "foot") return this.move.y;
    return Math.abs(this.move.y) > STICK_STEP ? Math.sign(this.move.y) : 0;
  }

  get stickActive(): boolean {
    return this.stickPointer !== null && Math.hypot(this.move.x, this.move.y) > 0;
  }

  isHeld(h: Hold): boolean {
    return this.held.has(h);
  }

  /** The left mouse button's work: the fire button, or a finger on the world while digging on foot. */
  get leftDown(): boolean {
    return this.held.has("fire") || (this.mode === "foot" && this.tool === "dig" && this.worldDown);
  }

  /** The right mouse button's work: a finger on the world while placing on foot. */
  get rightDown(): boolean {
    return this.mode === "foot" && this.tool === "place" && this.worldDown;
  }

  // ---- fingers on the world (the canvas), passed on by Input

  fingerDown(e: PointerEvent): void {
    if (!this.enabled) {
      if (this.preference !== "auto") return;
      this.setEnabled(true);
    }
    this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.fingers.size === 1) {
      this.worldDown = true;
      this.onPointer(e.clientX, e.clientY);
    } else {
      // A second finger: zooming, not digging.
      this.worldDown = false;
      this.pinchDist = this.spread();
    }
  }

  fingerMove(e: PointerEvent): void {
    const f = this.fingers.get(e.pointerId);
    if (!f) return;
    f.x = e.clientX;
    f.y = e.clientY;
    if (this.fingers.size >= 2) {
      const d = this.spread();
      if (d > 0 && this.pinchDist > 0) this.onZoom(this.pinchDist / d);
      this.pinchDist = d;
    } else this.onPointer(e.clientX, e.clientY);
  }

  fingerUp(e: PointerEvent): void {
    if (!this.fingers.delete(e.pointerId)) return;
    // After a pinch the finger left over doesn't start digging.
    this.worldDown = false;
  }

  private spread(): number {
    const [a, b] = [...this.fingers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  // ---- the stick

  private wireStick(): void {
    const zone = this.stickZone;
    zone.addEventListener("pointerdown", (e) => {
      if (this.stickPointer !== null) return;
      this.stickPointer = e.pointerId;
      zone.setPointerCapture(e.pointerId);
      this.stickAt = { x: e.clientX, y: e.clientY };
      const r = zone.getBoundingClientRect();
      this.stick.style.left = `${e.clientX - r.left}px`;
      this.stick.style.top = `${e.clientY - r.top}px`;
      this.stick.classList.add("on");
      this.tilt(0, 0);
    });
    zone.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.stickPointer) return;
      let dx = e.clientX - this.stickAt.x;
      let dy = e.clientY - this.stickAt.y;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) {
        dx *= STICK_RADIUS / len;
        dy *= STICK_RADIUS / len;
      }
      this.tilt(dx, dy);
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== this.stickPointer) return;
      this.stickPointer = null;
      this.stick.classList.remove("on");
      this.tilt(0, 0);
    };
    zone.addEventListener("pointerup", end);
    zone.addEventListener("pointercancel", end);
  }

  /** The knob `dx`,`dy` px from the stick's middle (y down, as on screen). */
  private tilt(dx: number, dy: number): void {
    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
    const dead = (v: number) => (Math.abs(v) < STICK_DEAD ? 0 : (v - Math.sign(v) * STICK_DEAD) / (1 - STICK_DEAD));
    this.move.x = dead(dx / STICK_RADIUS);
    this.move.y = -dead(dy / STICK_RADIUS);
  }

  // ---- the buttons

  private buildButtons(): void {
    this.cluster.replaceChildren();
    this.buttons.clear();
    for (const def of BUTTONS[this.mode]) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `touch-btn${def.size ? ` ${def.size}` : ""}`;
      b.textContent = def.label;
      if (def.id === "tool") {
        this.showTool(b);
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          this.tool = this.tool === "dig" ? "place" : "dig";
          this.showTool(b);
        });
      } else if (def.hold) {
        const hold = def.hold;
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          b.setPointerCapture(e.pointerId);
          b.classList.add("down");
          this.held.add(hold);
        });
        const release = () => {
          b.classList.remove("down");
          this.held.delete(hold);
        };
        b.addEventListener("pointerup", release);
        b.addEventListener("pointercancel", release);
      } else if (def.press || def.leave) {
        const action = def.press;
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          b.classList.add("down");
          if (action) this.onPress(action);
          else this.onLeave();
        });
        const release = () => b.classList.remove("down");
        b.addEventListener("pointerup", release);
        b.addEventListener("pointercancel", release);
      }
      this.buttons.set(def.id, b);
      this.cluster.append(b);
    }
  }

  private showTool(b: HTMLButtonElement): void {
    b.textContent = this.tool === "dig" ? "⛏ Dig" : "▦ Place";
    b.classList.toggle("on", this.tool === "place");
  }

  private smallButton(label: string, title: string, go: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "touch-btn small";
    b.textContent = label;
    b.title = title;
    b.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      go();
    });
    return b;
  }

  /** Nothing held: the buttons are being rebuilt, or the controls hidden. */
  private releaseAll(): void {
    this.held.clear();
    this.fingers.clear();
    this.worldDown = false;
    this.stickPointer = null;
    this.stick.classList.remove("on");
    this.tilt(0, 0);
    for (const b of this.buttons.values()) b.classList.remove("down");
  }
}

/** Full screen, and sideways where the browser lets a page ask (Android does; iPhones don't). */
async function goFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else {
      await document.documentElement.requestFullscreen();
      const o = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
      await o.lock?.("landscape").catch(() => {});
    }
  } catch {
    // Not allowed here: never mind.
  }
}
